import React from 'react';
import HelpComposer from './HelpComposer';
import HelpHoursEditor, { HelpHours } from './HelpHours';
import HelpRequestList from './HelpRequestList';
import { helpStatuses, accountRoles, HelpAudience } from './helpPresentation';
import service from '../services/helpCenterService';
export default function HelpWorkspaceView({ model, listProps }) {
  const {
    t,
    admin,
    master,
    config,
    configError,
    settings,
    setSettings,
    setNotice,
    setCreating,
    setSelected,
    closePreview,
    canCreate,
    welcome,
    setWelcome,
    previewAccount,
    scoped,
    error,
    setError,
    refresh,
    notice,
    setConfig,
    selected,
    creating,
    identity,
    send,
    request,
    actionBusy,
    canAssign,
    assignees,
    mutate,
    canChangeStatus,
    user,
    threadBox,
    nearBottom,
    setNewMessages,
    markVisibleRead,
    threadLoading,
    messages,
    date,
    attachmentBusy,
    attachment,
    newMessages,
    scrollEnd,
    canReply,
    contact,
    preview,
    previewDialog,
    previewKey,
  } = model;
  return (
    <div className={`help-center${scoped ? ' hc-distributor' : ''}`}>
      <header className="hc-workspace-header">
        <div className="hc-heading">
          <h1>{t('Help Center')}</h1>
          <HelpHours config={configError ? null : config} />
        </div>
        <div className="hc-header-actions">
          {admin && config && (
            <button
              onClick={() => {
                setSettings(!settings);
                setNotice('');
                setCreating(false);
                setSelected(null);
                closePreview();
              }}
            >
              {t('Manage support hours')}
            </button>
          )}
          {!admin && (
            <button
              onClick={() => {
                setCreating(true);
                setNotice('');
                setSelected(null);
                setSettings(false);
                setError('');
                closePreview();
              }}
              className="hc-primary"
              disabled={!canCreate}
            >
              {t('New request')}
            </button>
          )}
        </div>
      </header>
      {welcome && !admin && (
        <section className="hc-feature-welcome" aria-labelledby="hc-feature-welcome-title">
          <div className="hc-feature-welcome-icon" aria-hidden="true">
            ?
          </div>
          <div className="hc-feature-welcome-copy">
            <h2 id="hc-feature-welcome-title">{t('Welcome to your new Help Center')}</h2>
            <p>
              {t(
                'Need a hand? Send a message directly to MedEffects admins, attach files or photos, and follow every reply here.'
              )}
            </p>
          </div>
          <button type="button" className="hc-primary" onClick={() => setWelcome(false)}>
            {t('Got it')}
          </button>
        </section>
      )}
      {(previewAccount || config?.readOnly || config?.writesEnabled === false) && (
        <p role="status" className="hc-scope-notice">
          {t('Read-only access. Requests and attachments can be viewed; messaging is unavailable.')}
        </p>
      )}
      {scoped && (
        <p className="hc-scope-notice">
          {t(
            master
              ? 'Your requests and clinic requests are shared with MedEffects admins.'
              : 'Your conversation is shared with your distributor and MedEffects admins. Other clinics cannot see it.'
          )}
        </p>
      )}
      {admin && (
        <p className="hc-scope-notice">
          {t(
            'All direct and distributor requests appear in this inbox. Every reply is shared with conversation participants.'
          )}
        </p>
      )}
      {config?.enabled === false && (
        <p role="status" className="hc-error">
          {t(
            'Help Center messaging is temporarily unavailable. Please use the contact options below.'
          )}
        </p>
      )}
      {error && (
        <div role="alert" className="hc-error">
          {error}
          <button
            onClick={() => {
              setError('');
              refresh().catch(() => setError(t('Could not refresh. Please try again.')));
            }}
          >
            {t('Refresh')}
          </button>
        </div>
      )}
      {notice && (
        <div className="hc-success hc-dismissible" role="status">
          <span>{notice}</span>
          <button
            type="button"
            className="hc-dismiss"
            aria-label={t('Dismiss notification')}
            title={t('Dismiss notification')}
            onClick={() => setNotice('')}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      )}
      {settings && config ? (
        <HelpHoursEditor config={config} onSaved={setConfig} onClose={() => setSettings(false)} />
      ) : (
        <>
          <div className={`hc-layout ${selected || creating ? 'hc-detail-open' : ''}`}>
            <HelpRequestList {...listProps} />
            <section
              className={`hc-detail${creating ? ' hc-creating' : ''}`}
              aria-label={t(creating ? 'New request' : 'Conversation')}
            >
              {creating ? (
                <>
                  <header className="hc-detail-head">
                    <button className="hc-back" onClick={() => setCreating(false)}>
                      {t('Back to requests')}
                    </button>
                    <h2>{t('New request')}</h2>
                    <p>{t('Tell us what you need. MedEffects admins will reply here.')}</p>
                  </header>
                  {canCreate && (
                    <HelpComposer
                      key={`${identity}:new`}
                      draftKey={`${identity}:new`}
                      creating
                      config={config}
                      onSend={send}
                    />
                  )}
                </>
              ) : selected ? (
                <>
                  <header className="hc-detail-head">
                    <button
                      className="hc-back"
                      onClick={() => {
                        setSelected(null);
                        closePreview();
                      }}
                    >
                      {t('Back to requests')}
                    </button>
                    {request && (
                      <>
                        <div className="hc-row-meta">
                          <span>{request.reference}</span>
                          <span className={`hc-status hc-${request.status}`}>
                            {t(helpStatuses[request.status])}
                          </span>
                        </div>
                        <h2>{request.subject}</h2>
                        <p className="hc-account-details">
                          <strong>{request.requester.name}</strong>
                          {admin && (
                            <>
                              {accountRoles[request.requester.role] && (
                                <span className="hc-account-role">
                                  {t(accountRoles[request.requester.role])}
                                </span>
                              )}
                              {request.requester.email && (
                                <span className="hc-account-email">{request.requester.email}</span>
                              )}
                            </>
                          )}
                        </p>
                        <HelpAudience request={request} />
                        <div className="hc-thread-actions">
                          {canAssign && (
                            <label>
                              {t('Assigned admin')}
                              <select
                                disabled={actionBusy}
                                value={request.assignedTo?._id || ''}
                                onChange={(e) =>
                                  mutate(() =>
                                    service.assign(
                                      request._id,
                                      e.target.value || null,
                                      request.revision
                                    )
                                  )
                                }
                              >
                                <option value="">{t('Unassigned')}</option>
                                {assignees.map((person) => (
                                  <option key={person._id} value={person._id}>
                                    {person.name}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                          {canChangeStatus && (
                            <button
                              disabled={actionBusy}
                              onClick={() =>
                                mutate(() =>
                                  service.status(
                                    request._id,
                                    request.status === 'resolved' ? 'needs_support' : 'resolved',
                                    request.revision
                                  )
                                )
                              }
                            >
                              {t(
                                request.status === 'resolved' ? 'Reopen request' : 'Resolve request'
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </header>
                  <div
                    className="hc-messages"
                    ref={threadBox}
                    onScroll={() => {
                      const node = threadBox.current;
                      nearBottom.current =
                        node.scrollHeight - node.scrollTop - node.clientHeight < 80;
                      if (nearBottom.current) {
                        setNewMessages(false);
                        markVisibleRead();
                      }
                    }}
                    aria-label={t('Messages')}
                  >
                    {threadLoading && <p role="status">{t('Loading conversation…')}</p>}
                    {messages.map((message) => (
                      <article
                        key={message._id}
                        className={`hc-message hc-message-${message.senderKind}${message.sender._id && message.sender._id === (user._id || user.id) ? ' hc-message-own' : ''}`}
                      >
                        <div className="hc-message-meta">
                          <span className="hc-sender-identity">
                            <strong>{message.sender.name}</strong>
                            {admin ? (
                              <>
                                {accountRoles[message.sender.role] && (
                                  <span className="hc-account-role">
                                    {t(accountRoles[message.sender.role])}
                                  </span>
                                )}
                                {message.sender.email && (
                                  <span className="hc-account-email">{message.sender.email}</span>
                                )}
                              </>
                            ) : (
                              <span>
                                {message.senderKind === 'support'
                                  ? t('MedEffects support')
                                  : message.senderKind === 'distributor'
                                    ? t('Distributor')
                                    : t('Requester')}
                              </span>
                            )}
                          </span>
                          <time dateTime={message.createdAt}>{date(message.createdAt)}</time>
                        </div>
                        {message.text && <p>{message.text}</p>}
                        {message.attachments.length > 0 && (
                          <ul className="hc-message-files">
                            {message.attachments.map((file) => (
                              <li key={file._id}>
                                <button disabled={attachmentBusy} onClick={() => attachment(file)}>
                                  {file.mimeType === 'application/pdf' ? 'PDF' : t('Photo')} ·{' '}
                                  {file.name}
                                  <small>
                                    {Math.ceil(file.size / 1024)} KB · {t('Open attachment')}
                                  </small>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <small className="hc-sent">{t('Sent')}</small>
                      </article>
                    ))}
                  </div>
                  {attachmentBusy && (
                    <p role="status" className="hc-scope-notice">
                      {t('Opening attachment...')}
                    </p>
                  )}
                  {newMessages && (
                    <button className="hc-new-messages" onClick={scrollEnd}>
                      {t('New messages ↓')}
                    </button>
                  )}
                  {request && !canReply && (
                    <p role="status" className="hc-scope-notice">
                      {t(
                        request.status === 'resolved'
                          ? 'This request is resolved. Contact support to reopen it.'
                          : 'Replies are unavailable for this request.'
                      )}
                    </p>
                  )}
                  {request && canReply && config?.enabled && (
                    <HelpComposer
                      key={`${identity}:${selected}`}
                      draftKey={`${identity}:${selected}`}
                      config={config}
                      expectedRevision={request.revision}
                      canResolve={
                        canChangeStatus &&
                        (admin || master) &&
                        request.requester._id !== (user._id || user.id)
                      }
                      onSend={send}
                    />
                  )}
                </>
              ) : (
                <div className="hc-empty hc-welcome">
                  <div className="hc-welcome-icon" aria-hidden="true">
                    ?
                  </div>
                  <h2>{t(admin ? 'Support inbox' : 'A little help, right here')}</h2>
                  <p>
                    {t(
                      admin
                        ? 'Select a request to read and reply to the conversation.'
                        : 'Choose a request to read the conversation, or start a new one.'
                    )}
                  </p>
                  {!admin && (
                    <button
                      className="hc-primary"
                      disabled={!canCreate}
                      onClick={() => {
                        setNotice('');
                        setCreating(true);
                      }}
                    >
                      {t('Start a request')}
                    </button>
                  )}
                </div>
              )}
            </section>
          </div>
        </>
      )}
      <footer className="hc-contact">
        <strong>{t('Other ways to reach us')}</strong>
        {contact?.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email) && (
          <a href={`mailto:${contact.email}`}>{contact.email}</a>
        )}
        {contact?.phone && (
          <a href={`tel:${contact.phone.replace(/[^+\d]/g, '')}`}>{contact.phone}</a>
        )}
        <a href="/support.html" target="_blank" rel="noreferrer">
          {t('Support contact information')}
        </a>
        {contact?.content && <p>{contact.content}</p>}
      </footer>
      {preview && (
        <div className="hc-modal-backdrop" onClick={closePreview}>
          <section
            ref={previewDialog}
            role="dialog"
            aria-modal="true"
            aria-label={t('Attachment preview')}
            className="hc-attachment-preview"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={previewKey}
          >
            <header>
              <h2>{preview.name}</h2>
              <a href={preview.url} download={preview.name}>
                {t('Download')}
              </a>
              <button autoFocus onClick={closePreview}>
                {t('Close preview')}
              </button>
            </header>
            {preview.mimeType === 'application/pdf' ? (
              <iframe title={t('PDF attachment')} src={preview.url} />
            ) : (
              <img src={preview.url} alt={preview.name} />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
